import { create } from "zustand";
import { http } from "@/lib/api";
import type { Api } from "../types/api";
import type { Database } from "../types/db";
import type { Cache } from "../types/cache";

type HealthState = {
  apiStatus: string;
  dbStatus: string;
  cacheStatus: string;
  apiLoading: boolean;
  dbLoading: boolean;
  cacheLoading: boolean;
};

type HealthActions = {
  list: (signal?: AbortSignal) => Promise<void>;
  getDatabase: (signal?: AbortSignal) => Promise<void>;
  getCache: (signal?: AbortSignal) => Promise<void>;
  getApi: (signal?: AbortSignal) => Promise<void>;
};

export const useHealthStore = create<HealthState & HealthActions>((set) => ({
  apiStatus: "Offline",
  dbStatus: "Offline",
  cacheStatus: "Offline",
  apiLoading: false,
  dbLoading: false,
  cacheLoading: false,

  async list(signal) {
    set({ apiLoading: true, dbLoading: true, cacheLoading: true });
    try {
      await this.getApi(signal);
      await this.getDatabase(signal);
      await this.getCache(signal);
      set({ apiLoading: false, dbLoading: false, cacheLoading: false });
    } catch {
      set({ apiLoading: false, dbLoading: false, cacheLoading: false });
    }
  },

  async getDatabase(signal) {
    set({ dbLoading: true });
    try {
      const data = await http<Database>("/api/v1/health/db", {}, signal);
      set({ dbStatus: data.db, dbLoading: false });
    } catch {
      set({ dbStatus: "Offline", dbLoading: false });
    }
  },

  async getCache(signal) {
    set({ cacheLoading: true });
    try {
      const data = await http<Cache>("/api/v1/health/cache", {}, signal);
      set({ cacheStatus: data.cache, cacheLoading: false });
    } catch {
      set({ cacheStatus: "Offline", cacheLoading: false });
    }
  },

  async getApi(signal) {
    set({ apiLoading: true });
    try {
      const data = await http<Api>("/api/v1/health", {}, signal);
      set({ apiStatus: data.status, apiLoading: false });
    } catch {
      set({ apiStatus: "Offline", apiLoading: false });
    }
  },

}));
